import { Component, Inject, Injectable, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators, ReactiveFormsModule, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CommonModule } from '@angular/common';
import { GuestPackageType, ProductType, Prices, BACKEND_PRODUCT_MAP } from '@utils/prices';
import { Subscription } from 'rxjs';

export interface WalkInDialogData {
  productTypes: { value: string; label: string; }[];
  packageTypes: { value: string; label: string; }[];
}

export interface WalkInChairAssignment {
  productType: string;
  quantity: number;
  startDate: Date;
  endDate: Date;
  productPrice: number;
}

export interface WalkInFormData {
  firstName: string;
  lastName: string;
  email: string;
  packageType: GuestPackageType;
  chairAssignments: WalkInChairAssignment[];
}

@Component({
  selector: 'app-walkin-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [provideNativeDateAdapter()],
  template: `
    <h2 mat-dialog-title>
      <mat-icon>person_add</mat-icon>
      Kreiraj Walk-in Rezervaciju
    </h2>
    <mat-dialog-content>
      <form [formGroup]="walkInForm" class="walkin-form">
        <!-- Name Fields -->
        <div class="name-row mt-2">
          <mat-form-field appearance="outline">
            <mat-label>Ime</mat-label>
            <input matInput formControlName="firstName" placeholder="Unesite ime" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Prezime</mat-label>
            <input matInput formControlName="lastName" placeholder="Unesite prezime" />
          </mat-form-field>
        </div>

        <!-- Email -->
        <mat-form-field appearance="outline">
          <mat-label>Email</mat-label>
          <input matInput formControlName="email" type="email" placeholder="email@example.com" />
          <mat-icon matPrefix>email</mat-icon>
        </mat-form-field>

        <!-- Package Type -->
<!--        <mat-form-field appearance="outline">-->
<!--          <mat-label>Tip paketa</mat-label>-->
<!--          <mat-select formControlName="packageType" placeholder="Odaberite paket">-->
<!--            <mat-option *ngFor="let pkg of data.packageTypes" [value]="pkg.value">-->
<!--              {{ pkg.label }}-->
<!--            </mat-option>-->
<!--          </mat-select>-->
<!--          <mat-icon matPrefix>card_membership</mat-icon>-->
<!--        </mat-form-field>-->

        <!-- Chair Assignments Section -->
        <div class="chair-assignments-section">
          <div class="section-header">
            <h3>Ležaljke</h3>
            <button
              type="button"
              mat-raised-button
              color="accent"
              (click)="addChairAssignment()"
              class="add-btn">
              <mat-icon>add</mat-icon>
              Dodaj ležaljku
            </button>
          </div>

          <!-- Chair Assignments List -->
          <div formArrayName="chairAssignments" class="chair-assignments-list">
            <div *ngFor="let assignment of chairAssignmentsArray.controls; let i = index" [formGroupName]="i" class="chair-assignment-item">
              <div class="assignment-header">
                <span class="assignment-number">Ležaljka #{{ i + 1 }}</span>
                <button
                  type="button"
                  mat-icon-button
                  color="warn"
                  (click)="removeChairAssignment(i)"
                  [disabled]="chairAssignmentsArray.length === 1"
                  class="remove-btn">
                  <mat-icon>delete</mat-icon>
                </button>
              </div>

              <!-- Product Type -->
              <mat-form-field appearance="outline">
                <mat-label>Tip ležaljke</mat-label>
                <mat-select formControlName="productType" placeholder="Odaberite tip ležaljke">
                  <mat-option *ngFor="let type of data.productTypes" [value]="type.label">
                    {{ type.label }}
                  </mat-option>
                </mat-select>
                <mat-icon matPrefix>chair</mat-icon>
              </mat-form-field>

              <!-- Quantity -->
              <mat-form-field appearance="outline">
                <mat-label>Količina</mat-label>
                <input matInput formControlName="quantity" type="number" min="1" placeholder="1" />
                <mat-icon matPrefix>format_list_numbered</mat-icon>
              </mat-form-field>

              <!-- Product Price -->
              <mat-form-field appearance="outline">
                <mat-label>Cijena po ležaljki (€)</mat-label>
                <input matInput formControlName="productPrice" type="number" min="0" placeholder="0" />
                <mat-icon matPrefix>euro</mat-icon>
                <span matSuffix *ngIf="chairAssignmentsArray.at(i).get('productPrice')?.value">€</span>
              </mat-form-field>

              <!-- Dates -->
              <div class="date-row">
                <mat-form-field appearance="outline">
                  <mat-label>Datum dolaska</mat-label>
                  <input matInput [matDatepicker]="startPicker" formControlName="startDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                  <mat-datepicker #startPicker></mat-datepicker>
                  <mat-icon matPrefix>login</mat-icon>
                </mat-form-field>

                <mat-form-field appearance="outline">
                  <mat-label>Datum odlaska</mat-label>
                  <input matInput [matDatepicker]="endPicker" formControlName="endDate" />
                  <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                  <mat-datepicker #endPicker></mat-datepicker>
                  <mat-icon matPrefix>logout</mat-icon>
                </mat-form-field>
              </div>

              <div *ngIf="chairAssignmentsArray.at(i).errors?.['invalidDateRange']" class="validation-error">
                Datum dolaska mora biti manji od datuma odlaska.
              </div>
            </div>
          </div>

          <div *ngIf="chairAssignmentsArray.errors?.['minLength']" class="validation-error">
            Najmanje jedna ležaljka mora biti dodana.
          </div>
        </div>

        <!-- Summary -->
        <div class="summary-section" *ngIf="walkInForm.valid">
          <h3>Sažetak</h3>
          <div class="summary-row">
            <span>Gost:</span>
            <strong>{{ walkInForm.value.firstName }} {{ walkInForm.value.lastName }}</strong>
          </div>
          <div class="summary-row">
            <span>Email:</span>
            <strong>{{ walkInForm.value.email }}</strong>
          </div>
<!--          <div class="summary-row">-->
<!--            <span>Paket:</span>-->
<!--            <strong>{{ getPackageLabel(walkInForm.value.packageType) }}</strong>-->
<!--          </div>-->
          <div class="summary-divider"></div>
          <div class="summary-items">
            <div *ngFor="let assignment of walkInForm.value.chairAssignments; let i = index" class="summary-item">
              <div class="summary-item-header">
                <strong>Ležaljka #{{ i + 1 }}</strong>
              </div>
              <div class="summary-row">
                <span>Tip:</span>
                <strong>{{ getProductLabel(assignment.productType) }}</strong>
              </div>
              <div class="summary-row">
                <span>Količina:</span>
                <strong>{{ assignment.quantity }}</strong>
              </div>
              <div class="summary-row">
                <span>Dolazak:</span>
                <strong>{{ formatDate(assignment.startDate) }}</strong>
              </div>
              <div class="summary-row">
                <span>Odlazak:</span>
                <strong>{{ formatDate(assignment.endDate) }}</strong>
              </div>
              <div class="summary-row">
                <span>Dana:</span>
                <strong>{{ calculateDays(assignment.startDate, assignment.endDate) }}</strong>
              </div>
              <div class="summary-row">
                <span>Cijena po ležaljki:</span>
                <strong>{{ assignment.productPrice ? assignment.productPrice + ' €' : '-' }}</strong>
              </div>
              <div class="summary-row total-row">
                <span>Ukupno:</span>
                <strong>{{ calculateTotal(assignment) }} €</strong>
              </div>
            </div>
          </div>
          <div class="summary-divider"></div>
          <div class="grand-total-row">
            <span>Ukupan iznos:</span>
            <strong>{{ calculateGrandTotal() }} €</strong>
          </div>
        </div>
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Otkaži</button>
      <button
        mat-raised-button
        color="primary"
        (click)="onSubmit()"
        [disabled]="walkInForm.invalid || loading"
      >
        <mat-icon *ngIf="!loading">check</mat-icon>
        <mat-spinner *ngIf="loading" diameter="20"></mat-spinner>
        <span *ngIf="!loading">Kreiraj rezervaciju</span>
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .walkin-form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 400px;
    }

    .name-row, .date-row {
      display: flex;
      gap: 1rem;
    }

    .name-row mat-form-field, .date-row mat-form-field {
      flex: 1;
    }

    .chair-assignments-section {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .section-header h3 {
      margin: 0;
      font-size: 16px;
      color: #333;
    }

    .add-btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .chair-assignments-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .chair-assignment-item {
      padding: 1rem;
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      background: #fafafa;
    }

    .assignment-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
    }

    .assignment-number {
      font-weight: 500;
      color: #333;
    }

    .remove-btn {
      width: 36px;
      height: 36px;
    }

    .remove-btn:disabled {
      opacity: 0.3;
    }

    .validation-error {
      color: #f44336;
      font-size: 12px;
      margin-top: 0.5rem;
    }

    .summary-section {
      margin-top: 1rem;
      padding: 1rem;
      background: #f5f5f5;
      border-radius: 8px;
    }

    .summary-section h3 {
      margin: 0 0 0.75rem 0;
      font-size: 16px;
      color: #333;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 0;
      font-size: 14px;
    }

    .summary-row span {
      color: #666;
    }

    .summary-row strong {
      color: #333;
    }

    .summary-divider {
      border-top: 1px solid #e0e0e0;
      margin: 0.75rem 0;
    }

    .summary-items {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .summary-item {
      padding: 0.75rem;
      background: white;
      border-radius: 4px;
      border: 1px solid #e0e0e0;
    }

    .summary-item-header {
      margin-bottom: 0.5rem;
      color: #1976d2;
    }

    .total-row {
      margin-top: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px dashed #e0e0e0;
      font-weight: 600;
    }

    .total-row strong {
      color: #2e7d32;
    }

    .grand-total-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      font-size: 16px;
      font-weight: 600;
      background: #e8f5e9;
      border-radius: 4px;
      padding: 0.75rem;
    }

    .grand-total-row strong {
      color: #2e7d32;
      font-size: 18px;
    }
  `]
})
export class WalkInDialogComponent implements OnInit {
  walkInForm: FormGroup;
  loading: boolean = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<WalkInDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WalkInDialogData
  ) {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    this.walkInForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      packageType: [GuestPackageType.EXTERNAL],
      chairAssignments: this.fb.array([
        this.createChairAssignmentGroup(today, tomorrow)
      ], this.validateMinLength(1))
    });
  }

  get chairAssignmentsArray(): FormArray {
    return this.walkInForm.get('chairAssignments') as FormArray;
  }

  ngOnInit(): void {
    // Set initial price for the first chair assignment
    this.setInitialPriceForAssignment(0);

    // Track product type changes to update prices
    this.chairAssignmentsArray.controls.forEach((control, index) => {
      this.trackProductTypeChanges(index);
    });
  }

  setInitialPriceForAssignment(index: number): void {
    const assignment = this.chairAssignmentsArray.at(index);
    const productType = assignment.get('productType')?.value;
    if (productType) {
      const price = this.calculateInitialPrice(productType, this.walkInForm.get('packageType')?.value);
      assignment.get('productPrice')?.setValue(price);
    }
  }

  trackProductTypeChanges(index: number): void {
    const assignment = this.chairAssignmentsArray.at(index);
    const productTypeControl = assignment.get('productType');

    if (productTypeControl) {
      const sub = productTypeControl.valueChanges.subscribe(productType => {
        if (productType) {
          this.updatePriceForAssignment(index, productType);
        }
      });
      this.subscriptions.push(sub);
    }
  }

  createChairAssignmentGroup(startDate: Date, endDate: Date): FormGroup {
    return this.fb.group({
      productType: ['', Validators.required],
      quantity: [1, [Validators.required, Validators.min(1)]],
      startDate: [startDate, Validators.required],
      endDate: [endDate, Validators.required],
      productPrice: [0, [Validators.required, Validators.min(0)]]
    }, { validators: this.dateRangeValidator() });
  }

  dateRangeValidator(): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      if (!(group instanceof FormGroup)) {
        return null;
      }

      const startDate = group.get('startDate')?.value;
      const endDate = group.get('endDate')?.value;

      if (!startDate || !endDate) {
        return null;
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      // Clear time part for accurate date comparison
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      if (start >= end) {
        return { invalidDateRange: true };
      }

      return null;
    };
  }

  addChairAssignment(): void {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    this.chairAssignmentsArray.push(this.createChairAssignmentGroup(today, tomorrow));

    // Track product type changes for the new assignment
    const newIndex = this.chairAssignmentsArray.length - 1;
    this.trackProductTypeChanges(newIndex);
  }

  removeChairAssignment(index: number): void {
    if (this.chairAssignmentsArray.length > 1) {
      this.chairAssignmentsArray.removeAt(index);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  validateMinLength(min: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control instanceof FormArray && control.length < min) {
        return { minLength: true };
      }
      return null;
    };
  }

  getProductLabel(value: string): string {
    return value;
  }

  formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  calculateDays(startDate: Date, endDate: Date): number {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  calculateTotal(assignment: WalkInChairAssignment): number {
    if (!assignment.productPrice || !assignment.quantity) return 0;
    const days = this.calculateDays(assignment.startDate, assignment.endDate);
    return assignment.productPrice * assignment.quantity * days;
  }

  calculateGrandTotal(): number {
    const assignments = this.walkInForm.value.chairAssignments as WalkInChairAssignment[];
    if (!assignments) return 0;
    return assignments.reduce((sum, assignment) => sum + this.calculateTotal(assignment), 0);
  }

  calculateInitialPrice(productType: string, packageType: GuestPackageType): number {
    const labelKey = productType.toLowerCase();
    const mappedType = BACKEND_PRODUCT_MAP[labelKey] || productType as ProductType;
    try {
      return Prices.getLoungerPrice(mappedType, packageType);
    } catch {
      return 0;
    }
  }

  updatePriceForAssignment(index: number, productType: string): void {
    const packageType = this.walkInForm.get('packageType')?.value;
    const price = this.calculateInitialPrice(productType, packageType);
    const assignment = this.chairAssignmentsArray.at(index);
    assignment.get('productPrice')?.setValue(price);
  }

  onSubmit(): void {
    if (this.walkInForm.invalid) return;

    const formValue = this.walkInForm.value;

    const formData: WalkInFormData = {
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      email: formValue.email,
      packageType: formValue.packageType,
      chairAssignments: formValue.chairAssignments
    };

    this.dialogRef.close(formData);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
